import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { AuthService } from '../../../features/auth/auth.service';

export interface MaintenanceData {
  message:          string;
  title:            string;
  retryAfter:       number;
  estimatedEndTime: string;
  notify_roles:     string[];
  notes_impact?:    string;
  start_time?:      string;
  end_time?:        string;
}

@Injectable({ providedIn: 'root' })
export class MaintenanceWindowService {
  private auth = inject(AuthService);

  private subject = new BehaviorSubject<MaintenanceData | null>(null);
  maintenance$ = this.subject.asObservable();

  constructor() {
    this.restoreFromStorage();
  }

  // ─── Show maintenance ─────────────────────────────────────────────
  show(data: MaintenanceData) {
    this.subject.next(data);
    localStorage.setItem('maintenance_data', JSON.stringify(data));
    this.setupAutoClear(data);
  }

  // ─── Clear maintenance ────────────────────────────────────────────
  clear() {
    this.subject.next(null);
    localStorage.removeItem('maintenance_data');
  }

  // ─── Get current value ────────────────────────────────────────────
  get value(): MaintenanceData | null {
    return this.subject.value;
  }

  // ─── Decide whether to show maintenance ───────────────────────────
  shouldShow(data: MaintenanceData): boolean {
    const roles = data.notify_roles ?? [];

    // ✅ If no roles specified → show for everyone
    if (roles.length === 0) return true;

    const role = this.auth.getUserRole();

    // ❗ If role not available → don't show restricted maintenance
    if (!role) return false;

    return roles.includes(role);
  }

  // ─── Restore from localStorage (after refresh) ────────────────────
  private restoreFromStorage() {
    const stored = localStorage.getItem('maintenance_data');
    if (stored) {
      try {
        const data: MaintenanceData = JSON.parse(stored);
        this.subject.next(data);
        this.setupAutoClear(data);
      } catch (e) {
        console.error('Invalid maintenance data in storage');
        localStorage.removeItem('maintenance_data');
      }
    }
  }

  // ─── Auto clear when maintenance ends ─────────────────────────────
  private setupAutoClear(data: MaintenanceData) {
    if (!data.estimatedEndTime) return;

    const endTime = new Date(data.estimatedEndTime).getTime();
    const now = Date.now();

    if (endTime > now) {
      setTimeout(() => {
        this.clear();
      }, endTime - now);
    } else {
      // Already expired
      this.clear();
    }
  }
}